import {
  createCapabilitySuggestionDecisionEvent,
  createCapabilitySuggestionSubmittedEvent,
  createMachineSubmittedForReviewEvent,
  createMockEventNotificationList,
  createProjectFactoryAddedEvent,
  type CapabilitySuggestionDecisionEventDetails,
  type CapabilitySuggestionSubmittedEventDetails,
  type EventNotification,
  type MachineSubmittedForReviewEventDetails,
} from '@indutrust/notification-model';

const EVENT_NOTIFICATION_SEED_KEY = '__idt_eventNotificationSeed__' as const;
const EVENT_NOTIFICATION_NEXT_ID_KEY =
  '__idt_eventNotificationNextId__' as const;

type EventNotificationSeedGlobals = typeof globalThis & {
  [EVENT_NOTIFICATION_SEED_KEY]?: EventNotification[];
  [EVENT_NOTIFICATION_NEXT_ID_KEY]?: number;
};

const seedGlobals = globalThis as EventNotificationSeedGlobals;

/**
 * Mutable MSW seed for `${baseUrl}/events`.
 *
 * Anchored on `globalThis` so Vite/browser bundles that import
 * `@indutrust/notification-data` from multiple domain libs share one
 * array (capability/machine appends stay visible to `GET /events`).
 */
export const eventNotificationSeed: EventNotification[] =
  seedGlobals[EVENT_NOTIFICATION_SEED_KEY] ??
  createMockEventNotificationList(5).map((entry, index): EventNotification => ({
    ...entry,
    id: index + 1,
  }));

seedGlobals[EVENT_NOTIFICATION_SEED_KEY] = eventNotificationSeed;

let nextPositiveEventId =
  seedGlobals[EVENT_NOTIFICATION_NEXT_ID_KEY] ??
  eventNotificationSeed.length + 1;

function allocateEventId(): number {
  const id = nextPositiveEventId;
  nextPositiveEventId += 1;
  seedGlobals[EVENT_NOTIFICATION_NEXT_ID_KEY] = nextPositiveEventId;
  return id;
}

/** Prepends a server-shaped `project_factory_added` row to the demo events seed. */
export function appendProjectFactoryAddedEvent(
  projectId: number
): EventNotification {
  const duplicateIndex = eventNotificationSeed.findIndex(
    (entry) =>
      entry.event_type === 'project_factory_added' &&
      entry.target_id === projectId
  );

  if (duplicateIndex !== -1) {
    const [existing] = eventNotificationSeed.splice(duplicateIndex, 1);
    const refreshed: EventNotification = {
      ...existing,
      created_at: new Date().toISOString(),
      read: false,
    };
    eventNotificationSeed.unshift(refreshed);
    return refreshed;
  }

  const created: EventNotification = {
    ...createProjectFactoryAddedEvent(projectId),
    id: allocateEventId(),
  };
  eventNotificationSeed.unshift(created);
  return created;
}

/** Prepends a `machine_submitted_for_review` row for admin catalog review. */
export function appendMachineSubmittedForReviewEvent(
  catalogEntryId: number,
  details?: MachineSubmittedForReviewEventDetails
): EventNotification {
  const duplicateIndex = eventNotificationSeed.findIndex(
    (entry) =>
      entry.event_type === 'machine_submitted_for_review' &&
      entry.target_id === catalogEntryId
  );

  if (duplicateIndex !== -1) {
    const [existing] = eventNotificationSeed.splice(duplicateIndex, 1);
    const refreshed: EventNotification = {
      ...existing,
      ...createMachineSubmittedForReviewEvent(catalogEntryId, details),
      id: existing.id,
      created_at: new Date().toISOString(),
      read: false,
    };
    eventNotificationSeed.unshift(refreshed);
    return refreshed;
  }

  const created: EventNotification = {
    ...createMachineSubmittedForReviewEvent(catalogEntryId, details),
    id: allocateEventId(),
  };
  eventNotificationSeed.unshift(created);
  return created;
}

/**
 * Prepends a `capability_suggestion_submitted` row for the admin queue.
 * Deduplicates by suggestion {@link target_id}.
 */
export function appendCapabilitySuggestionSubmittedEvent(
  suggestionId: number,
  details?: CapabilitySuggestionSubmittedEventDetails
): EventNotification {
  const duplicateIndex = eventNotificationSeed.findIndex(
    (entry) =>
      entry.event_type === 'capability_suggestion_submitted' &&
      entry.target_id === suggestionId
  );

  if (duplicateIndex !== -1) {
    const [existing] = eventNotificationSeed.splice(duplicateIndex, 1);
    const refreshed: EventNotification = {
      ...existing,
      ...createCapabilitySuggestionSubmittedEvent(suggestionId, details),
      id: existing.id,
      created_at: new Date().toISOString(),
      read: false,
    };
    eventNotificationSeed.unshift(refreshed);
    return refreshed;
  }

  const created: EventNotification = {
    ...createCapabilitySuggestionSubmittedEvent(suggestionId, details),
    id: allocateEventId(),
  };
  eventNotificationSeed.unshift(created);
  return created;
}

/**
 * Prepends a supplier outcome event for an approved or rejected suggestion.
 * {@link factoryId} is stored as {@link EventNotification.target_id} for edit routing.
 */
export function appendCapabilitySuggestionDecisionEvent(
  factoryId: number,
  decision: 'approved' | 'rejected',
  details?: CapabilitySuggestionDecisionEventDetails
): EventNotification {
  const eventType =
    decision === 'approved'
      ? 'capability_suggestion_approved'
      : 'capability_suggestion_rejected';
  const suggestionName = details?.suggestionName?.trim();

  const duplicateIndex = eventNotificationSeed.findIndex((entry) => {
    if (entry.event_type !== eventType || entry.target_id !== factoryId) {
      return false;
    }
    if (!suggestionName) {
      return true;
    }
    return entry.payload?.['suggestion_name'] === suggestionName;
  });

  if (duplicateIndex !== -1) {
    const [existing] = eventNotificationSeed.splice(duplicateIndex, 1);
    const refreshed: EventNotification = {
      ...existing,
      ...createCapabilitySuggestionDecisionEvent(factoryId, decision, details),
      id: existing.id,
      created_at: new Date().toISOString(),
      read: false,
    };
    eventNotificationSeed.unshift(refreshed);
    return refreshed;
  }

  const created: EventNotification = {
    ...createCapabilitySuggestionDecisionEvent(factoryId, decision, details),
    id: allocateEventId(),
  };
  eventNotificationSeed.unshift(created);
  return created;
}

/**
 * Drops the admin `capability_suggestion_submitted` alert once the suggestion
 * is approved or rejected so the queue bell entry does not linger.
 */
export function resolveCapabilitySuggestionSubmittedEvent(
  suggestionId: number
): void {
  const index = eventNotificationSeed.findIndex(
    (entry) =>
      entry.event_type === 'capability_suggestion_submitted' &&
      entry.target_id === suggestionId
  );
  if (index !== -1) {
    eventNotificationSeed.splice(index, 1);
  }
}
